import React from 'react'
import Loader from '../../../components/Loader'
import {
  Grid, PagingPanel,
  Table,
  TableHeaderRow,
  Toolbar,
} from '@devexpress/dx-react-grid-bootstrap4'
import {
  CustomPaging,
  PagingState,
  SortingState,
} from '@devexpress/dx-react-grid'
import Utils from '../../../utils/Utils'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowDown,
  faArrowUp,
  faDownload,
  faFileExport,
} from '@fortawesome/free-solid-svg-icons'
import { Tooltip } from '@mui/material'
import { json2excel } from 'js2excel'
import dayjs from 'dayjs'
import ProjectController from '../../../controllers/ProjectController'

const ProjectPaymentTabView = ({
  projectId,
  isLoading,
  payments,
  totalElements,
  sorting,
  pagination,
  handleSorting,
  handlePageChange,
  handlePageSizeChange
}) => {

  const columns = [
    {
      name: 'id',
      title: 'Payment ID',
      orderable: true,
      hideable: true,
      getCellValue: (row) => row.id,
    },
    {
      name: 'user.sid',
      title: 'User',
      orderable: true,
      hideable: true,
      getCellValue: (row) => row.user.sid,
    },
    {
      name: 'amount',
      title: 'Amount',
      orderable: true,
      hideable: true,
      getCellValue: (row) => row.amount,
    },
    {
      name: 'status',
      title: 'Status',
      orderable: true,
      hideable: true,
      getCellValue: (row) => row.status,
    },
    {
      name: 'comment',
      title: 'Comment',
      orderable: true,
      hideable: true,
      getCellValue: (row) => row.comment,
    },
    {
      name: 'paidAt',
      title: 'Timestamp Paid',
      orderable: true,
      hideable: true,
      getCellValue: (row) => {
        return Utils.isNull(row.paidAt) ? '' :
          moment(row.paidAt).format('YYYY-MM-DD HH:mm')},
    },
    {
      name: 'createdAt',
      title: 'Timestamp created',
      orderable: true,
      hideable: true,
      getCellValue: (row) => {
        return Utils.isNull(row.createdAt) ? '' :
          moment(row.createdAt).format('YYYY-MM-DD HH:mm')},
    },
  ];

  const cellComponent = (props) => {
    return <Table.Cell {...props} className='tw-p-[6px]' />;
  };

  const sortableColumns = columns.filter((col) => col.orderable).map((col) => col.name);

  const TableHeaderRowSortLabelComponent = (props) => {
    if (sortableColumns.includes(props.column.name)) {
      const direction = props.direction;

      return (
        <TableHeaderRow.SortLabel {...props} className='sortable-column-label'>
          <div className='tw-flex tw-items-center tw-text-xs md:tw-text-sm'>
            {props.children}
            <div className='ml-1'>
              {!Utils.isNull(direction) ? (
                <FontAwesomeIcon icon={direction === 'asc' ? faArrowUp : faArrowDown} />
              ) : (
                <FontAwesomeIcon icon={faArrowUp} className='tw-opacity-40' />
              )}
            </div>
          </div>
        </TableHeaderRow.SortLabel>
      );
    } else {
      return <span className='font-bold tw-text-xs md:tw-text-sm'>{props.column.title}</span>;
    }
  };

  const toolbarRoot = () => {
    return (
      <div className='table-toolbar'>
        <div>
          <span className='tw-font-medium tw-text-base'>Total Payments:</span>
          <span className='tw-ml-2 tw-text-base'>{totalElements}</span>
        </div>
        <div className='tw-flex tw-items-center tw-gap-2'>
          {/*create a function to download*/}
          <span className='toolbar-button' onClick={() => downloadPayments('table')}>
            <Tooltip title="Export to CSV the payments that are currently on the table's page.">
              <FontAwesomeIcon icon={faFileExport} className='icon' />
            </Tooltip>
          </span>
          <span className='toolbar-button' onClick={() => downloadPayments('all')}>
            <Tooltip title="Export to CSV all the payments.">
              <FontAwesomeIcon icon={faDownload} className='icon' />
            </Tooltip>
          </span>
        </div>
      </div>
    );
  };

  const downloadPayments = async (source) => {
    let excelPayments = [];
    let paymentsToDownload;

    const convertAndExport = async (payments) => {
      for (let i = 0; i < payments.length; i++) {
        excelPayments.push(await paymentsJsonToExcelFormat(payments[i]));
      }
      json2excel({ data: excelPayments, name: projectId + ' tasks', formatdate: 'dd/mm/yyyy' });
    };

    if (source === 'table') {
      paymentsToDownload = payments;
      await convertAndExport(paymentsToDownload);
    } else if (source === 'all') {
      paymentsToDownload = (
        await ProjectController.getPayments(projectId, {
          page: 0,
          size: 100000,
        })
      ).content;
      await convertAndExport(paymentsToDownload);
    }
  };

  const paymentsJsonToExcelFormat = async (payment) => {
    return {
      'Payment ID': payment.id,
      'User SID': payment.user?.sid ?? null,
      'Amount': payment.amount,
      'Status': payment.status,
      'Comment': payment.comment ?? '',
      'Date Paid': payment.paidAt
        ? dayjs(payment.paidAt).format('YYYY-MM-DD HH:mm')
        : null,
      'Date Created': payment.createdAt
        ? dayjs(payment.createdAt).format('YYYY-MM-DD HH:mm')
        : null,
    };
  };



  return(
    <>
      <div className='tw-relative tw-mt-10'>
        {isLoading && <Loader isLoading={isLoading} />}
        <Grid rows={payments} columns={columns}>
          <SortingState sorting={sorting} onSortingChange={handleSorting} />
          <PagingState
            currentPage={pagination.page}
            pageSize={pagination.size}
            onCurrentPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
          <CustomPaging totalCount={totalElements} />
          <Table
            columnExtensions={[
              {
                width: 'auto',
                columnName: 'id',
              },
              {
                width: 'auto',
                columnName: 'user.sid',
              },
              {
                width: 'auto',
                columnName: 'amount',
              },
              {
                width: 'auto',
                columnName: 'status',
                wordWrapEnabled: true,
              },
              {
                width: 'auto',
                columnName: 'comment',
                wordWrapEnabled: true,
              },
              {
                width: 'auto',
                columnName: 'paidAt',
                wordWrapEnabled: true,
              },
              {
                width: 'auto',
                columnName: 'createdAt',
                wordWrapEnabled: true,
              },
            ]}
            cellComponent={cellComponent}
          />
          <TableHeaderRow
            showSortingControls={true}
            sortLabelComponent={TableHeaderRowSortLabelComponent}
          />
          <Toolbar rootComponent={toolbarRoot} />
          <PagingPanel pageSizes={[10, 25, 50, 100, 500, 1000]} />
        </Grid>
      </div>
    </>
  )
}
export default ProjectPaymentTabView;